import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './ReviewerDashboard.css';

const ReviewerDashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const email = localStorage.getItem('email');

    // Static important dates - matches AdminHomePage
    const importantDates = [
        {
            label: "Abstract Submission Deadline",
            date: "7th December",
            year: "2025",
            status: "active",
            icon: "📝"
        },
        {
            label: "Notification of Acceptance",
            date: "14th December", 
            year: "2025",
            status: "upcoming",
            icon: "📧"
        },
        {
            label: "Registration Deadline",
            date: "20th December",
            year: "2025",
            status: "upcoming", 
            icon: "💳"
        },
        {
            label: "Conference Dates",
            date: "15 May",
            year: "2026",
            status: "upcoming",
            icon: "🎯"
        }
    ];

    useEffect(() => {
        if (email) {
            fetchDashboardData();
        } else {
            setError('No email found in localStorage');
            setLoading(false);
        }
    }, [email]);

    const fetchDashboardData = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/reviewer/dashboard-stats?email=${email}`
            );
            
            if (!response.ok) {
                throw new Error('Failed to fetch dashboard data');
            }

            const data = await response.json();
            setDashboardData(data.data);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err.message);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="reviewer-dashboard-wrapper">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="reviewer-dashboard-wrapper">
                <div className="error-container">
                    <div className="error-icon">⚠️</div>
                    <h3>Error Loading Dashboard</h3>
                    <p className="error-message">{error}</p>
                    <button onClick={() => window.location.reload()} className="retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const { reviewer, statistics, recentReviews } = dashboardData || {};

    return (
        <div className="reviewer-dashboard-wrapper">
            <div className="reviewer-dashboard-container">
                {/* Header Section */}
                <div className="header-section">
                    <h1 className="page-title">Reviewer Dashboard</h1>
                    <p className="page-subtitle">Welcome back, {reviewer?.firstName} {reviewer?.lastName}</p>
                </div>

                {/* Reviewer Info Card */}
                <div className="reviewer-info-card">
                    <div className="reviewer-avatar">
                        {reviewer?.firstName?.[0]}{reviewer?.lastName?.[0]}
                    </div>
                    <div className="reviewer-details">
                        <h2 className="reviewer-name">{reviewer?.firstName} {reviewer?.lastName}</h2>
                        <p className="reviewer-email">{reviewer?.email}</p>
                        {reviewer?.domains && reviewer.domains.length > 0 && (
                            <div className="reviewer-domains">
                                <span className="domains-label">Expertise:</span>
                                {reviewer.domains.map((domain, idx) => (
                                    <span key={idx} className="domain-badge">{domain}</span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="reviewer-action">
                        <Link to="/reviewer/review-papers" className="view-papers-btn">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View All Papers
                        </Link>
                    </div>
                </div>

                {/* Statistics Section */}
                <div className="stats-section">
                    <h2 className="section-title">Review Statistics</h2>
                    <div className="stats-grid">
                        <div className="stat-card stat-card-total">
                            <div className="stat-icon">📋</div>
                            <div className="stat-content">
                                <div className="stat-number">{statistics?.totalAssigned || 0}</div>
                                <div className="stat-label">Total Assigned</div>
                                <div className="stat-description">Papers assigned to you</div>
                            </div>
                        </div>

                        <div className="stat-card stat-card-completed">
                            <div className="stat-icon">✅</div>
                            <div className="stat-content">
                                <div className="stat-number">{statistics?.totalReviewed || 0}</div>
                                <div className="stat-label">Completed</div>
                                <div className="stat-description">Reviews submitted</div>
                            </div>
                        </div>

                        <div className="stat-card stat-card-pending">
                            <div className="stat-icon">⏳</div>
                            <div className="stat-content">
                                <div className="stat-number">{statistics?.pendingReviews || 0}</div>
                                <div className="stat-label">Pending</div>
                                <div className="stat-description">Awaiting your review</div>
                            </div>
                        </div>

                        <div className="stat-card stat-card-rate">
                            <div className="stat-icon">📈</div>
                            <div className="stat-content">
                                <div className="stat-number">{statistics?.completionRate || 0}%</div>
                                <div className="stat-label">Completion Rate</div>
                                <div className="stat-description">Your progress</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Important Dates Section */}
                <div className="dates-section">
                    <h2 className="section-title">Important Dates</h2>
                    <div className="dates-timeline">
                        {importantDates.map((dateItem, index) => (
                            <div key={index} className={`date-item ${dateItem.status}`}>
                                <div className="date-content">
                                    <div className="date-icon">{dateItem.icon}</div>
                                    <div className="date-text">
                                        <strong>{dateItem.label} <span> - </span></strong>
                                        <p>{dateItem.date}, {dateItem.year}</p>
                                    </div>
                                    <span className={`date-badge ${dateItem.status}`}>
                                        {dateItem.status === 'active' ? 'Active' : 'Upcoming'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recommendations Breakdown */}
                <div className="recommendations-section">
                    <h2 className="section-title">Review Recommendations</h2>
                    <div className="recommendations-grid">
                        <div className="recommendation-card rec-accept">
                            <div className="rec-icon">✓</div>
                            <div className="rec-number">{statistics?.recommendations?.accept || 0}</div>
                            <div className="rec-label">Accepted</div>
                            <div className="rec-bar">
                                <div 
                                    className="rec-bar-fill rec-bar-accept" 
                                    style={{
                                        width: statistics?.totalReviewed > 0 
                                            ? `${(statistics.recommendations.accept / statistics.totalReviewed * 100)}%`
                                            : '0%'
                                    }}
                                ></div>
                            </div>
                        </div>

                        <div className="recommendation-card rec-reject">
                            <div className="rec-icon">✗</div>
                            <div className="rec-number">{statistics?.recommendations?.reject || 0}</div>
                            <div className="rec-label">Rejected</div>
                            <div className="rec-bar">
                                <div 
                                    className="rec-bar-fill rec-bar-reject" 
                                    style={{
                                        width: statistics?.totalReviewed > 0 
                                            ? `${(statistics.recommendations.reject / statistics.totalReviewed * 100)}%`
                                            : '0%'
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Reviews */}
                <div className="recent-reviews-section">
                    <div className="section-header-with-link">
                        <div>
                            <h2 className="section-title">Recent Reviews</h2>
                            <p className="section-subtitle">Your latest paper reviews</p>
                        </div>
                    </div>
                    {recentReviews && recentReviews.length > 0 ? (
                        <div className="recent-reviews-list">
                            {recentReviews.map((review, index) => (
                                <div key={index} className="review-item">
                                    <div className="review-header">
                                        <div className="review-title-section">
                                            <span className="paper-id-badge">Paper #{review.paperID}</span>
                                            <h3 className="review-paper-title">{review.title}</h3>
                                        </div>
                                        <span className={`recommendation-badge rec-${review.recommendation?.toLowerCase()}`}>
                                            {review.recommendation}
                                        </span>
                                    </div>
                                    <div className="review-footer">
                                        <span className="review-date">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            {new Date(review.reviewDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            })}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-reviews">
                            <svg className="no-reviews-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <p>No reviews submitted yet</p>
                        </div>
                    )}
                </div>

                {/* Quick Actions */}
                <div className="quick-actions-section">
                    <h2 className="section-title">Quick Actions</h2>
                    <div className="actions-grid">
                        <Link to="/reviewer/review-papers" className="action-card">
                            <div className="action-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3 className="action-label">View Assigned Papers</h3>
                            <p className="action-description">See all papers assigned to you for review</p>
                        </Link>

                        <div className="action-card action-card-disabled">
                            <div className="action-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <h3 className="action-label">Profile Settings</h3>
                            <p className="action-description">Update your profile information</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReviewerDashboard;
